import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
import EditOutlined from '@mui/icons-material/EditOutlined'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'

const SummaryRow = ({ label, children }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    justifyContent="space-between"
    alignItems={{ xs: 'flex-start', sm: 'center' }}
    gap={0.5}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    {typeof children === 'string' ? (
      <Typography variant="body2" fontWeight={600} textAlign="right">
        {children}
      </Typography>
    ) : (
      children
    )}
  </Stack>
)

// First step of the appraisal: the employee reviews their most recent
// occupational health screening and either confirms nothing has changed
// (creating a fresh dated copy) or goes to the full form to update it.
const OccupationalHealthStep = ({ employeeID, onConfirmed }) => {
  const [loading, setLoading] = useState(true)
  const [previous, setPrevious] = useState(null)
  const [confirming, setConfirming] = useState(false)
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)

  useEffect(() => {
    API.get(`/occupational-health/employee/${employeeID}`)
      .then(async (res) => {
        const latestID = res.status === 200 && res.data.submissions?.[0]?._id
        if (latestID) {
          const full = await API.get(`/occupational-health/${latestID}`)
          if (full.status === 200) setPrevious(full.data)
        }
      })
      .finally(() => setLoading(false))
  }, [employeeID])

  const confirmNoChanges = () => {
    setConfirming(true)
    API.post('/occupational-health/reconfirm')
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Occupational Health Confirmed' })
          onConfirmed()
        } else {
          setToast({
            severity: 'error',
            message:
              res.data?.error ?? 'Something went wrong confirming your details.',
          })
        }
      })
      .finally(() => setConfirming(false))
  }

  if (loading) return <LinearProgress sx={{ my: 2 }} />

  if (!previous) {
    return (
      <Stack gap={2} py={1}>
        <Alert severity="info">
          You haven't completed an occupational health screening yet. Please
          complete one to continue your appraisal — you'll be brought back to
          this appraisal afterwards.
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/my-occupational-health')}
        >
          Complete My Screening
        </Button>
      </Stack>
    )
  }

  const notImmunised = (previous.immunisations ?? [])
    .filter((item) => !item.immunised)
    .map((item) => item.label)
  const unable = (previous.fitness ?? [])
    .filter((item) => !item.able)
    .map((item) => item.label)
  const risks = (previous.covidRisks ?? [])
    .filter((risk) => risk.selected)
    .map((risk) => risk.label)

  return (
    <Stack gap={1.5} py={1}>
      <Typography variant="body2" color="text.secondary">
        This is a summary of your last occupational health screening, completed{' '}
        <strong>{moment(previous.submittedAt).format('Do MMM YYYY')}</strong>.
        Has anything changed?
      </Typography>

      <Stack
        gap={1}
        divider={<Divider flexItem />}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}
      >
        <SummaryRow label="Height / Weight / BMI">
          {`${previous.biometrics?.heightCm ?? '—'} cm / ${previous.biometrics?.weightKg ?? '—'} kg / BMI ${previous.biometrics?.bmi ?? '—'}`}
        </SummaryRow>
        <SummaryRow label="GP / Surgery">
          {previous.gpDetails || 'Not provided'}
        </SummaryRow>
        <SummaryRow label="Disability">
          {previous.disability?.has
            ? previous.disability.details || 'Yes'
            : 'No'}
        </SummaryRow>
        <SummaryRow label="Allergies">
          {previous.allergies?.has ? previous.allergies.details || 'Yes' : 'No'}
        </SummaryRow>
        <SummaryRow label="Regular medications">
          {previous.medications || 'None listed'}
        </SummaryRow>
        <SummaryRow label="Currently pregnant">
          {previous.pregnant ? 'Yes' : 'No'}
        </SummaryRow>
        <SummaryRow label="Corrective lenses to drive">
          {previous.correctiveLenses ? 'Yes' : 'No'}
        </SummaryRow>
        <SummaryRow label="Medical conditions">
          {previous.conditions?.length > 0 ? (
            <Stack direction="row" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
              {previous.conditions.map((condition) => (
                <Chip key={condition} size="small" label={condition} />
              ))}
            </Stack>
          ) : (
            'None declared'
          )}
        </SummaryRow>
        <SummaryRow label="Not immunised against">
          {notImmunised.length > 0 ? notImmunised.join(', ') : 'None'}
        </SummaryRow>
        <SummaryRow label="Fitness tasks unable to complete">
          {unable.length > 0 ? unable.join(', ') : 'None'}
        </SummaryRow>
        <SummaryRow label="COVID-19 risks">
          {risks.length > 0 ? risks.join(', ') : 'None identified'}
        </SummaryRow>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircleOutline />}
          disabled={confirming}
          onClick={confirmNoChanges}
          fullWidth
        >
          {confirming ? 'Confirming...' : 'Nothing Has Changed — Confirm'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<EditOutlined />}
          onClick={() => navigate('/my-occupational-health')}
          fullWidth
        >
          Something Has Changed — Update
        </Button>
      </Stack>
    </Stack>
  )
}

export default OccupationalHealthStep
